import { ElementApi, RangeApi } from 'platejs';
import type { TElement } from 'platejs';
import type { PlateEditor } from 'platejs/react';

const ZERO_WIDTH_CHARACTERS = /[\u200B\uFEFF]/g;

/**
 * True when text is effectively empty for formatting-affordance purposes: '',
 * whitespace-only, or zero-width characters only (Plate's empty-paragraph marker
 * `\u200B`, or the `\uFEFF` Slate renders in empty leaves). JS `.trim()` strips
 * neither, so empty editors that normalize to them would otherwise look "non-empty".
 */
export function isBlankText(text: string): boolean {
  return text.replace(ZERO_WIDTH_CHARACTERS, '').trim() === '';
}

/**
 * True when the editor holds nothing the user wrote: blank text and no inline elements,
 * such as mention or skill chips, which carry no text of their own.
 */
export function getIsEditorBlank(editor: PlateEditor): boolean {
  return isBlankText(editor.api.string([])) && !editor.children.some(getHasChildElement);
}

/**
 * Serialized markdown with an all-blank document collapsed to ''. Callers that
 * gate UI or requests on "has the user written anything" must use this: Plate
 * serializes blank paragraphs as `\u200B`, which survives `.trim()`, so a
 * visually empty editor otherwise reads as having content.
 */
export function normalizeBlankMarkdown(markdown: string): string {
  return isBlankText(markdown) ? '' : markdown;
}

/**
 * The block the empty-paragraph actions anchors to, or undefined when the
 * toggle shouldn't render:
 * - Caret collapsed in a blank block → that block
 * - No selection yet, but the whole document is blank → the first block
 * - Expanded selection → nothing (the selection toolbar owns that state)
 */
export function getEmptyParagraphActionsBlock(editor: PlateEditor) {
  const selection = editor.selection;
  if (selection && RangeApi.isExpanded(selection)) return undefined;
  if (!selection) {
    if (!isBlankText(editor.api.string([]))) return undefined;
    return editor.api.block({ at: [0] });
  }

  if (
    editor.api.above({
      at: selection,
      match: node => ElementApi.isElement(node) && (node.type === 'td' || node.type === 'th'),
    })
  )
    return undefined;
  const entry = editor.api.block({ at: selection });
  if (!entry || getHasChildElement(entry[0])) return undefined;

  return isBlankText(editor.api.string(entry[1])) ? entry : undefined;
}

/** Whether the empty-paragraph actions should render. */
export function getShouldShowEmptyParagraphActions(editor: PlateEditor): boolean {
  return Boolean(getEmptyParagraphActionsBlock(editor));
}

function getHasChildElement(block: TElement): boolean {
  return block.children.some(child => ElementApi.isElement(child));
}
